import { verifyError } from "Components/Common/helpers/verifyError";
import { auth } from "Configs/firebase";
import { useApp } from "Providers/AppProvider";
import { useTranslation } from "Providers/TranslationProvider";
import {
	User,
	onAuthStateChanged,
	signInWithCustomToken,
	signInWithEmailAndPassword,
} from "firebase/auth";
import { useEffect } from "react";
import { create } from "zustand";

interface State {
	isInitializing: boolean;
	user?: User;
	error?: Error;
	signIn: (email: string, password: string) => Promise<void>;
	signOut: () => Promise<void>;
	reset: () => void;
}

export const useAuthUser = create<State>((set, get) => ({
	isInitializing: true,
	signIn: async (email, password) => {
		await signInWithEmailAndPassword(auth, email, password);
	},
	signOut: async () => {
		await auth.signOut();
	},
	reset: () => {
		set({
			isInitializing: true,
			user: undefined,
			error: undefined,
		});
	},
}));

export const useSubscribeToAuthUser = () => {
	const { token } = useApp();
	const { t } = useTranslation();

	useEffect(() => {
		return useAuthUser.getState().reset;
	}, []);

	useEffect(() => {
		if (!token) return;

		signInWithCustomToken(auth, token)
			.then(({ user }) =>
				useAuthUser.setState({
					user: user || undefined,
					isInitializing: false,
					error: undefined,
				})
			)
			.catch((error) => {
				if (error.code === "auth/invalid-custom-token") {
					error.message = t("ErrorView_Message_RestartApplication");
					error.token = token;
				}

				useAuthUser.setState({
					user: undefined,
					isInitializing: false,
					error: verifyError(error),
				});
			});
	}, [t, token]);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(
			auth,
			(nextUser) => {
				useAuthUser.setState({
					user: nextUser || undefined,
					isInitializing: token ? !nextUser : false,
				});
			},
			(error) => {
				useAuthUser.setState({
					error: verifyError(error),
					isInitializing: false,
				});
			}
		);

		return unsubscribe;
	}, [token]);
};
